import { isObject } from 'lodash-es';

import { NATURAL_SEARCH_REPLAY } from '../../constants/local_storage';
import type { StoredNaturalSearch } from './types';

// Session storage, so the replay survives a reload of the tab but never reaches
// a link opened elsewhere, where the same query may read differently.

export const storeNaturalSearch = (entry: StoredNaturalSearch): void => {
  sessionStorage.setItem(NATURAL_SEARCH_REPLAY, JSON.stringify(entry));
};

export const clearStoredNaturalSearch = (): void => {
  sessionStorage.removeItem(NATURAL_SEARCH_REPLAY);
};

const isStoredNaturalSearch = (
  value: unknown,
): value is StoredNaturalSearch => {
  if (!isObject(value) || !('query' in value) || !('replay' in value)) {
    return false;
  }
  const { query, replay } = value;
  if (typeof query !== 'string' || !isObject(replay)) {
    return false;
  }
  return (
    'filters' in replay &&
    isObject(replay.filters) &&
    'pagination' in replay &&
    isObject(replay.pagination) &&
    'supported' in replay.pagination &&
    typeof replay.pagination.supported === 'boolean'
  );
};

const parseStored = (raw: string | null): unknown => {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    // Written by an older build or by hand: a reload then runs the query again.
    return null;
  }
};

export const readStoredNaturalSearch = (): StoredNaturalSearch | null => {
  const value = parseStored(sessionStorage.getItem(NATURAL_SEARCH_REPLAY));
  if (!isStoredNaturalSearch(value)) {
    return null;
  }
  // Only what goes beside the answer, so a malformed one is dropped rather
  // than refusing the replay.
  const { interpretations, adjustments } = value;
  return {
    query: value.query,
    replay: value.replay,
    ...(isObject(interpretations) && Array.isArray(interpretations.list)
      ? {
          // Stored by a build that did not record a choice.
          interpretations: {
            ...interpretations,
            chosen: interpretations.chosen === true,
          },
        }
      : {}),
    ...(isObject(adjustments) &&
    Array.isArray(adjustments.added) &&
    Array.isArray(adjustments.paused)
      ? { adjustments }
      : {}),
  };
};
